import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { ConfigContext } from "../App";
import { useProductContent } from "../hooks/useQueries";
import { useLocation } from "react-router-dom";
import CarouselComponent from "../components/Carousel/Carousel";
import { dimColor, formatPrice, optimizeImageUrl, IMAGE_SIZES } from "../functions/utils";
import { BsFillCartDashFill } from "react-icons/bs";
import { BsCartPlusFill } from "react-icons/bs";
import Loading from "../components/Loading/Loading.js";
import { addToCart } from "../functions/addToCart.js";
import { TranslateContext } from "../App";
import config from "../configuration.json";
import Banner from "../components/Banner/Banner.js";

/**
 * Product page component.
 * Displays a single product with carousel, details, and add-to-cart functionality.
 * Uses React Query for cached data fetching.
 */
function Product() {
  const configuration = useContext(ConfigContext);
  const location = useLocation();
  const slug = location.pathname.substring(1);
  const [hovered, setHovered] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const language = useContext(TranslateContext).language;

  // Use React Query hook for cached product data
  const { data: product, isLoading } = useProductContent(slug);

  // Transform images for carousel with optimized URLs
  const carouselImages = React.useMemo(() => {
    if (!product?.images) return [];
    return product.images.map((image) => ({
      // Use product detail size for main carousel images
      src: optimizeImageUrl(image.asset.url, IMAGE_SIZES.productDetail),
      alt: product.title[language],
    }));
  }, [product, language]);

  useEffect(() => {
    if (product) {
      // Retrieve the cartItems array from local storage
      let cartItems = localStorage.getItem("cartItems");
      cartItems = cartItems ? JSON.parse(cartItems) : [];
      // Check if the current item is in the cart
      const isAddedToCart = cartItems.some((cartItem) => cartItem.id === product.id);
      // Update the addedToCart state based on the result
      setAddedToCart(isAddedToCart);
    } // Add this line
  }, [product]);

  const handleHover = (state) => {
    setHovered(state);
  };

  const handleAddToCart = () => {
    const isAddedToCart = addToCart(product);
    setAddedToCart(isAddedToCart);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    product && (
      <>
        <Helmet>
          <title>{product.title[language]}</title>
          <meta name="description" content={product.description[language]} />
        </Helmet>
        {configuration?.checkoutBanner?.show && (
          <div className="row bg-[#fcf6ef]">
            <div className="row-container">
              <Banner
                type={configuration?.checkoutBanner?.type || "warning"}
                message={configuration?.checkoutBanner?.message}
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="product">
            <CarouselComponent
              infiniteLoop={true}
              interval={3}
              images={carouselImages}
              axis={"horizontal"}
              autoPlay={false}
              bgColor={"white"}
              showArrows={true}
              height={"400px"}
              showIndicators={carouselImages.length > 1 ? true : false}
            />
            <div className="product-body">
              <div>
                <h1 className="h1-title text-start">{product.title[language]}</h1>
                <p>{product.description[language]}</p>
              </div>
              <div>
                <div className="product-price-container">
                  <p className="product-price">{formatPrice(product.price, language, config)}</p>
                </div>
                <button
                  className="add-to-cart-button"
                  onMouseEnter={() => handleHover(true)}
                  onMouseLeave={() => handleHover(false)}
                  style={{
                    backgroundColor:
                      product.quantity === 0 && !addedToCart
                        ? "gray"
                        : hovered
                        ? dimColor(configuration.colors?.main?.hex, 20)
                        : configuration.colors?.main?.hex,
                  }}
                  disabled={product.quantity === 0 && !addedToCart}
                  onClick={handleAddToCart}
                >
                  {addedToCart ? <BsFillCartDashFill size={18} /> : <BsCartPlusFill size={18} />}
                  {addedToCart ? config.texts.remove[language] : config.texts.add[language]}
                </button>
                {product.quantity === 0 && (
                  <p className="product-not-available-label">{config.texts.productIsOutOfStock[language]}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );
}

export default Product;
